import { useContext, useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import { UNSAFE_ViewTransitionContext } from 'react-router';
import { useAtomValue } from 'jotai';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';

const DESKTOP_VIEWPORT_QUERY = '(min-width: 768px)';

const subscribeToDesktopViewport = (onChange: () => void) => {
    const query = window.matchMedia(DESKTOP_VIEWPORT_QUERY);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
};

/**
 * Whether the viewport is at or above the md breakpoint. SSR assumes desktop;
 * the claim styles this gates are inert on the server, so the only effect of
 * the assumption is a correction after hydration on mobile.
 */
export const useIsDesktopViewport = (): boolean =>
    useSyncExternalStore(
        subscribeToDesktopViewport,
        () => window.matchMedia(DESKTOP_VIEWPORT_QUERY).matches,
        () => true,
    );

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const subscribeToReducedMotion = (onChange: () => void) => {
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
};

/**
 * Reduced-motion preference. When set, tiles skip the view-transition opt-in
 * entirely (no snapshot capture at all, not just neutralised animations —
 * the `prefers-reduced-motion` CSS guard remains as belt and braces), while
 * non-motion behaviour like the mobile app-mode chrome stays active.
 */
export const usePrefersReducedMotion = (): boolean =>
    useSyncExternalStore(
        subscribeToReducedMotion,
        () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
        () => false,
    );

/**
 * Snapshot names shared by a tile's media box on a listing and its morph
 * counterpart on the detail page (product gallery slide 0 / project hero).
 * During a navigation between the two, the browser morphs one box into the
 * other instead of cross-fading them with the rest of the page (styles in
 * packages/web `tailwind.css`).
 *
 * A document may only carry one element with a given name — a duplicate makes
 * the browser skip the transition (an instant swap, not a broken frame) — so
 * claim it only while a matching navigation is in flight. Names are unique
 * per content type, so a page can carry a product and a project claim at
 * once without conflict.
 */
export const PRODUCT_HERO_TRANSITION_NAME = 'product-hero';
export const PROJECT_HERO_TRANSITION_NAME = 'project-hero';
export const PROFESSIONAL_HERO_TRANSITION_NAME = 'professional-hero';

/**
 * Style for the element that should morph during a view transition whose
 * origin or destination pathname equals `to` — the tile whose own navigation
 * is in flight (either direction, so Back morphs symmetrically), never its
 * neighbours.
 *
 * Reads the router's view-transition context directly rather than through
 * `useViewTransitionState`, which requires a data router and throws without
 * one. The context defaults to `{ isTransitioning: false }`, so outside a
 * data router (Business Manager, unit tests) this is simply inert.
 */
export const useTileHeroViewTransitionStyle = (
    to: string | undefined,
    name: string = PRODUCT_HERO_TRANSITION_NAME,
): CSSProperties | undefined => {
    const enabled = useAtomValue(uiContextAtomViewTransitionsEnabled);
    const reducedMotion = usePrefersReducedMotion();
    const vt = useContext(UNSAFE_ViewTransitionContext);
    if (!enabled || reducedMotion || !vt.isTransitioning || !to) {
        return undefined;
    }

    const targetPathname = to.split(/[?#]/)[0];
    const matches =
        vt.currentLocation.pathname === targetPathname ||
        vt.nextLocation.pathname === targetPathname;
    return matches ? { viewTransitionName: name } : undefined;
};

/**
 * Image continuity for a cold cache: the destination gallery renders a
 * different (larger) rendition than the tile, so on first open the morph
 * would land on a grey placeholder until the high-res image decodes. The
 * tile's click handler records the exact rendition its <img> is showing —
 * already decoded, by definition — and the gallery paints it under its own
 * image until the real one arrives. Module state, so it survives the
 * client-side navigation and costs nothing on SSR or full loads.
 */
let pendingHeroImage: { pathname: string; src: string } | null = null;

export const setPendingHeroImage = (
    to: string | undefined,
    src: string | null | undefined,
): void => {
    if (!to || !src) return;
    pendingHeroImage = { pathname: to.split(/[?#]/)[0] ?? to, src };
};

export const getPendingHeroImage = (to: string): string | null => {
    const pending = pendingHeroImage;
    return pending && pending.pathname === to.split(/[?#]/)[0]
        ? pending.src
        : null;
};
